import React from 'react';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import { ReactComponent as TruckIcon } from 'assets/truck.svg';
import { Milestone } from 'models/shipments';
import { selectCalculatedEdd } from 'store/deliveryEstimate';
import { useAppSelector } from 'store/store';
import {
  formatTimeWindow,
  getCalculatedDeliveryDateText,
  getEddRangeText,
  getEddText,
  isToday,
} from 'utils/date.utils';

import { useCurrentArticle } from '../../hooks/shipment.hooks';
import InlineEdd from './InlineEdd/InlineEdd';
import { TRACKING_PANEL_EDD_TEST_ID, TRACKING_PANEL_EDD_TEXT_TEST_ID } from './Edd.test-ids';
import { tooltipContent } from './Edd.tooltip.config';

import styles from './Edd.module.scss';

const Edd: React.FC = () => {
  const {
    address,
    deliveredByDateISO,
    estimatedDeliveryDateRange: eddRange,
    estimatedDeliveryDateEligible: inlineEddEligible = false,
    timeWindow,
    events,
  } = useCurrentArticle() || {};
  const inlineEddCalculated = useAppSelector(selectCalculatedEdd);
  const isInternational = address?.internationalAddress ?? false;
  const currentMilestone = events?.[0]?.milestone ?? Milestone.PENDING;
  const { start, end, passedWindow, deliveryDate } = timeWindow ?? {};

  const isLateAfterTimeWindow = (passedWindow && deliveryDate && !isToday(deliveryDate)) || false;

  const displayEddText = () => {
    if (isLateAfterTimeWindow && deliveryDate) {
      return getEddText(deliveryDate);
    }

    if (start && end) {
      const windowStart = formatTimeWindow(start);
      const windowEnd = formatTimeWindow(end);
      return `Today, ${windowStart}–${windowEnd}`;
    }

    const eddRangeText = getEddRangeText(eddRange?.fromDate, eddRange?.toDate);
    if (eddRangeText) return eddRangeText;

    const eddSingleText = deliveredByDateISO && getEddText(deliveredByDateISO);
    if (eddSingleText) return eddSingleText;

    if (currentMilestone === Milestone.COMING_TODAY) return 'Today';

    if (isInternational) return `Dates can’t be calculated`;

    return 'Dates calculated soon';
  };

  const displayEddTooltip = (() => {
    if (inlineEddCalculated) return tooltipContent.inlineEdd;
    if (isLateAfterTimeWindow) return tooltipContent.edd;
    if (timeWindow?.start && timeWindow?.end) return tooltipContent.timeWindow;
    if ((eddRange?.fromDate && eddRange?.toDate) || deliveredByDateISO) return tooltipContent.edd;
    if (isInternational) return tooltipContent.international;
    if (inlineEddEligible) return null;
    return tooltipContent.default;
  })();

  return (
    <div className={styles.eddWrapper} data-testid={TRACKING_PANEL_EDD_TEST_ID}>
      <TruckIcon className={styles.truckIcon} />
      <div>
        <p className={styles.eddSubtitle}>EXPECTED ARRIVAL</p>
        <p className={styles.eddText} data-testid={TRACKING_PANEL_EDD_TEXT_TEST_ID}>
          {inlineEddEligible ? getCalculatedDeliveryDateText(inlineEddCalculated) : displayEddText()}
        </p>
        {inlineEddEligible && <InlineEdd />}
      </div>
      {!!displayEddTooltip && (
        <Infotip anchorClassName={styles.eddInfotipFloat} floatClassName={styles.eddInfotip}>
          {displayEddTooltip}
        </Infotip>
      )}
    </div>
  );
};

export default Edd;
